// src/components/checkout/PaymentForm.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import Button from '../common/Button';
import {
  createResellerLog,
  fetchData,
  fetchNgmktSell,
  fetchPaymentConfig,
  initializeMerchantPayment,
  verifyMerchantPayment,
} from '../../utils/api';

const GATEWAY_CURRENCIES = {
  paystack: 'NGN',
  flutterwave: 'NGN',
  afrimoney: 'SLE',
  orange_money: 'SLE',
  monime: 'SLE',
};

const GATEWAY_DETAILS = {
  paystack: {
    name: 'Paystack',
    description: 'Cards, bank transfer and USSD',
    icon: '/assets/icons/paystack.svg',
  },
  flutterwave: {
    name: 'Flutterwave',
    description: 'Cards, bank transfer and supported payment channels',
    icon: '/assets/icons/flutterwave.svg',
  },
  afrimoney: {
    name: 'AfriMoney',
    description: 'Pay from your AfriMoney mobile wallet',
    icon: '/assets/icons/afrimoney.svg',
  },
  orange_money: {
    name: 'Orange Money',
    description: 'Pay from your Orange Money mobile wallet',
    icon: '/assets/icons/orange-money.svg',
  },
  monime: {
    name: 'Monime',
    description: 'Secure Sierra Leone mobile-money checkout',
    icon: '/assets/icons/monime.svg',
  },
};

const normalizeGateway = (value = '') => {
  const gateway = String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

  const aliases = {
    paysack: 'paystack',
    pay_stack: 'paystack',
    flutter_wave: 'flutterwave',
    afri_money: 'afrimoney',
    afr_money: 'afrimoney',
    orange: 'orange_money',
    orangemoney: 'orange_money',
    monime_pay: 'monime',
  };

  return aliases[gateway] || gateway;
};

const firstPaymentConfig = (response) => {
  const candidates = [
    response,
    response?.data,
    response?.data?.data,
    response?.payment_configs,
    response?.data?.payment_configs,
    response?.configs,
  ];

  const list = candidates.find(Array.isArray);
  if (list) return list[0] || null;

  // Compatibility with the former single-object response.
  return response?.data && !Array.isArray(response.data)
    ? response.data
    : response || null;
};

const gatewayFromConfig = (config) =>
  normalizeGateway(
    config?.gateway ||
      config?.provider ||
      config?.payment_gateway ||
      config?.payment_method ||
      config?.name
  );

const publicKeyFromConfig = (config) =>
  config?.public_key ||
  config?.api_key ||
  config?.publicKey ||
  config?.key ||
  '';

const referenceFrom = (response, fallback) =>
  response?.reference ||
  response?.tx_ref ||
  response?.transaction_reference ||
  response?.data?.reference ||
  response?.data?.tx_ref ||
  fallback;

const hostedUrlFrom = (response) =>
  response?.checkout_url ||
  response?.payment_url ||
  response?.redirect_url ||
  response?.link ||
  response?.data?.checkout_url ||
  response?.data?.payment_url ||
  response?.data?.redirect_url ||
  response?.data?.link ||
  null;

const verificationPassed = (response) => {
  const data = response?.data?.data || response?.data || response || {};
  const status = String(
    data.payment_status || data.status || data.state || ''
  ).toLowerCase();

  return (
    data.verified === true ||
    data.success === true ||
    ['successful', 'success', 'paid', 'completed'].includes(status)
  );
};

const money = (value, currency) =>
  new Intl.NumberFormat(currency === 'NGN' ? 'en-NG' : 'en-SL', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

function PaymentForm({ total, onPaymentSuccess, shippingAddress, disabled }) {
  const { cart } = useCart();
  const { user, token: customerToken } = useAuth();

  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [vat, setVat] = useState(0);
  const [shippingFee, setShippingFee] = useState(0);

  const merchantItem = useMemo(
    () => cart.find((item) => item.merchant_id),
    [cart]
  );

  const merchantSubdomain = merchantItem?.merchant_subdomain;
  const visibilityToken =
    merchantItem?.merchant_auth_token ||
    merchantItem?.auth_token ||
    '';

  const resellerCustomerId = Number(
    String(visibilityToken).split('-')[0]
  );

  const gateway = gatewayFromConfig(paymentConfig);
  const currency = GATEWAY_CURRENCIES[gateway] || 'SLE';
  const gatewayDetails = GATEWAY_DETAILS[gateway];

  const subtotal = Number(total || 0);
  const vatRate = Number(vat || 0);
  const deliveryFee = Number(shippingFee || 0);
  const vatAmount = subtotal * (vatRate / 100);
  const finalTotal = subtotal + vatAmount + deliveryFee;

  // All Trackash/customer-facing values remain in naira.
  // Only Paystack's inline SDK receives the kobo equivalent.
  const paystackAmount = Math.round(finalTotal * 100);

  const customerEmail =
    shippingAddress?.email ||
    user?.tenant?.email ||
    user?.email ||
    'guest@example.com';
  const customerName =
    shippingAddress?.name ||
    user?.tenant?.full_name ||
    user?.name ||
    'Guest Customer';
  const customerPhone =
    shippingAddress?.phone ||
    user?.tenant?.telephone ||
    user?.phone_number ||
    '';

  useEffect(() => {
    const loadConfig = async () => {
      if (!merchantSubdomain) {
        setError('The restaurant for this order could not be identified.');
        setConfigLoading(false);
        return;
      }

      setConfigLoading(true);
      setError('');

      try {
        const [configResponse, business] = await Promise.all([
          fetchPaymentConfig(merchantSubdomain),
          fetchData('/website/business-details', {
            headers: { Client: merchantSubdomain },
            noCache: true,
          }),
        ]);

        const selectedConfig = firstPaymentConfig(configResponse);
        const selectedGateway = gatewayFromConfig(selectedConfig);

        if (!selectedConfig || !GATEWAY_DETAILS[selectedGateway]) {
          throw new Error(
            'This restaurant has no supported payment gateway configured.'
          );
        }

        setPaymentConfig(selectedConfig);
        setVat(Number(business?.fees?.vat?.vatRate || 0));
        setShippingFee(
          Number(business?.fees?.shipping?.shippingFee || 0)
        );
      } catch (loadError) {
        console.error('Payment settings error:', loadError);
        setPaymentConfig(null);
        setError(
          loadError?.response?.data?.message ||
            loadError?.message ||
            'Unable to load this restaurant’s payment settings.'
        );
      } finally {
        setConfigLoading(false);
      }
    };

    loadConfig();
  }, [merchantSubdomain]);

  const buildReference = useCallback(
    () =>
      `order_${merchantItem?.merchant_id || 'merchant'}_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 8)}`,
    [merchantItem?.merchant_id]
  );

  const completeOrder = useCallback(
    async (paymentResult, deliveryAddress = shippingAddress) => {
      const productsPayload = cart.map((item) => ({
        product_id: item.source_product_id ?? item.id,
        name: item.name,
        qty: item.quantity,
        price: item.price,
        has_stock: Number(item.has_stock || 0),
        stock: Number(item.stock || 0),
      }));

      const countryDefault = currency === 'SLE' ? 'Sierra Leone' : 'Nigeria';
      const fullShippingAddress = [
        (deliveryAddress?.street || deliveryAddress?.address || '').trim(),
        deliveryAddress?.city?.trim(),
        deliveryAddress?.state?.trim(),
        deliveryAddress?.zip?.trim(),
        deliveryAddress?.country?.trim() || countryDefault,
      ]
        .filter(Boolean)
        .join(', ');

      const paymentReference = referenceFrom(paymentResult);
      const salePayload = {
        customer_id: resellerCustomerId,
        delivery_email: customerEmail,
        to_default_address: Boolean(deliveryAddress?.to_default_address),
        contact_name: customerName,
        phone_number: customerPhone,
        contact_address: fullShippingAddress,
        products: productsPayload,
        amount: finalTotal,
        vat: Number(vat || 0),
        logistics: Number(shippingFee || 0),
        payment_method: gateway,
        payment_reference: paymentReference,
        payment_currency: currency,
        from_wallet: false,
        description: 'Online store purchase',
        platform: 'online',
        auth_code: 54517,
        is_order: false,
        part_payment_amount: 0,
        discount: 0,
        split: [
          {
            split_payment_method: null,
            split_payment_amount: null,
            bank_id: null,
          },
        ],
        type: 'full_payment',
        is_split_payment: 0,
        payment_type: null,
        start_date: null,
        end_date: null,
        posting_day: null,
      };

      const merchantSale = await fetchNgmktSell(
        salePayload,
        merchantSubdomain
      );
      const saleSucceeded =
        merchantSale?.status === 'success' ||
        merchantSale?.status_code === '000';

      if (!saleSucceeded || !merchantSale?.data?.id) {
        throw new Error(
          'Payment succeeded, but the restaurant could not record the order.'
        );
      }

      try {
        await createResellerLog(
          {
            merchant_id: Number(merchantItem.merchant_id),
            customer_id: user?.tenant?.id || user?.tenant_id || null,
            uuid: merchantSale.data.uuid,
            parent_uuid: null,
            log_type: 'online_sale',
            payload: merchantSale,
            request_payload: salePayload,
            response_payload: merchantSale,
            status: 'successful',
            attempts: 1,
            total_amount: Number(merchantSale.data.amount || finalTotal),
            item_count: cart.reduce(
              (count, item) => count + Number(item.quantity || 0),
              0
            ),
            currency,
            customer_name: customerName,
            processed_at: new Date().toISOString(),
          },
          customerToken
        );
      } catch (logError) {
        console.error('Completed sale could not be logged:', logError);
      }

      const orderId = merchantSale.data.id;
      const receiptNumber = `RV${String(orderId).padStart(8, '0')}`;

      onPaymentSuccess({
        ...paymentResult,
        status: 'success',
        gateway,
        currency,
        cart,
        fees: {
          subtotal: total,
          vat: vatAmount,
          shipping: shippingFee,
          total: finalTotal,
        },
        shippingAddress: deliveryAddress,
        transactionRef: paymentReference,
        receiptNumber,
        rawOrderId: orderId,
      });
    },
    [
      cart,
      currency,
      customerEmail,
      customerName,
      customerPhone,
      customerToken,
      finalTotal,
      gateway,
      merchantItem,
      merchantSubdomain,
      onPaymentSuccess,
      resellerCustomerId,
      shippingAddress,
      shippingFee,
      total,
      user,
      vat,
      vatAmount,
    ]
  );

  const verifyAndComplete = useCallback(
    async (reference, gatewayResponse = {}, deliveryAddress) => {
      const verification = await verifyMerchantPayment(
        {
          gateway,
          reference,
          amount: finalTotal,
          currency,
        },
        merchantSubdomain,
        paymentConfig?.verify_endpoint ||
          paymentConfig?.verification_endpoint ||
          '/website/payments/verify'
      );

      if (!verificationPassed(verification)) {
        throw new Error('The merchant could not verify this payment.');
      }

      await completeOrder(
        {
          ...gatewayResponse,
          ...verification,
          reference,
        },
        deliveryAddress
      );
    },
    [
      completeOrder,
      currency,
      finalTotal,
      gateway,
      merchantSubdomain,
      paymentConfig,
    ]
  );

  const pollVerification = useCallback(
    async (reference) => {
      const maximumAttempts = 40;

      for (let attempt = 1; attempt <= maximumAttempts; attempt += 1) {
        try {
          await verifyAndComplete(reference);
          return;
        } catch (verificationError) {
          if (attempt === maximumAttempts) throw verificationError;
          setNotice('Waiting for confirmation from your mobile wallet…');
          await new Promise((resolve) => window.setTimeout(resolve, 3000));
        }
      }
    },
    [verifyAndComplete]
  );

  const runHostedOrMobileGateway = async (reference) => {
    const initialization = await initializeMerchantPayment(
      {
        gateway,
        reference,
        amount: finalTotal,
        currency,
        customer: {
          name: customerName,
          email: customerEmail,
          phone_number: customerPhone,
        },
        callback_url: `${window.location.origin}/checkout?payment_return=1`,
        metadata: {
          merchant_id: merchantItem.merchant_id,
          customer_id: user?.tenant?.id || user?.tenant_id || null,
        },
      },
      merchantSubdomain,
      paymentConfig?.initialize_endpoint ||
        paymentConfig?.initialization_endpoint ||
        '/website/payments/initialize'
    );

    const returnedReference = referenceFrom(initialization, reference);
    const checkoutUrl = hostedUrlFrom(initialization);

    if (checkoutUrl) {
      localStorage.setItem(
        'pendingMerchantPayment',
        JSON.stringify({
          reference: returnedReference,
          gateway,
          merchantSubdomain,
          shippingAddress,
        })
      );
      window.location.assign(checkoutUrl);
      return;
    }

    if (verificationPassed(initialization)) {
      await verifyAndComplete(returnedReference, initialization);
      return;
    }

    setNotice(
      initialization?.message ||
        'Approve the payment request from your mobile wallet.'
    );
    await pollVerification(returnedReference);
  };

  useEffect(() => {
    if (!paymentConfig || !merchantSubdomain) return;

    const query = new URLSearchParams(window.location.search);
    if (query.get('payment_return') !== '1') return;

    let pending = null;
    try {
      pending = JSON.parse(
        localStorage.getItem('pendingMerchantPayment') || 'null'
      );
    } catch (_) {
      pending = null;
    }

    if (
      !pending?.reference ||
      pending.merchantSubdomain !== merchantSubdomain
    ) {
      setError('The returning payment could not be identified.');
      return;
    }

    let active = true;
    setIsLoading(true);
    setNotice('Confirming your payment with the restaurant…');

    verifyAndComplete(
      pending.reference,
      {},
      pending.shippingAddress || shippingAddress
    )
      .then(() => {
        localStorage.removeItem('pendingMerchantPayment');
      })
      .catch((returnError) => {
        if (!active) return;
        setError(
          returnError?.response?.data?.message ||
            returnError?.message ||
            'The returning payment could not be verified.'
        );
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    merchantSubdomain,
    paymentConfig,
    shippingAddress,
    verifyAndComplete,
  ]);

  const handlePayment = async (event) => {
    event.preventDefault();

    if (disabled || !shippingAddress) {
      setError('Please provide and save your delivery details first.');
      return;
    }

    if (!paymentConfig || !gatewayDetails) {
      setError('The restaurant’s payment gateway is not ready.');
      return;
    }

    if (
      !merchantItem ||
      !merchantSubdomain ||
      !visibilityToken ||
      !Number.isInteger(resellerCustomerId) ||
      resellerCustomerId <= 0
    ) {
      setError('This restaurant is not properly connected to the marketplace.');
      return;
    }

    setError('');
    setNotice('');
    setIsLoading(true);

    const reference = buildReference();
    const publicKey = publicKeyFromConfig(paymentConfig);

    try {
      if (gateway === 'paystack') {
        if (!publicKey || !window.PaystackPop) {
          throw new Error('Paystack is not properly configured.');
        }

        const handler = window.PaystackPop.setup({
          key: publicKey,
          email: customerEmail,
          amount: paystackAmount,
          currency: 'NGN',
          ref: reference,
          metadata: {
            customer_name: customerName,
            phone_number: customerPhone,
            merchant_id: merchantItem.merchant_id,
          },
          callback: async (response) => {
            try {
              await verifyAndComplete(response.reference, response);
            } catch (paymentError) {
              setError(paymentError.message);
            } finally {
              setIsLoading(false);
            }
          },
          onClose: () => {
            setNotice('Payment window was closed.');
            setIsLoading(false);
          },
        });

        handler.openIframe();
        return;
      }

      if (gateway === 'flutterwave') {
        if (!publicKey || !window.FlutterwaveCheckout) {
          throw new Error('Flutterwave is not properly configured.');
        }

        window.FlutterwaveCheckout({
          public_key: publicKey,
          tx_ref: reference,
          amount: finalTotal,
          currency: 'NGN',
          payment_options: paymentConfig?.payment_options || 'card,banktransfer,ussd',
          customer: {
            email: customerEmail,
            phone_number: customerPhone,
            name: customerName,
          },
          customizations: {
            title: merchantItem.merchant_name || 'Restaurant order',
            description: 'Online store purchase',
            logo: merchantItem.merchant_logo || undefined,
          },
          callback: async (response) => {
            try {
              await verifyAndComplete(
                response.tx_ref || reference,
                response
              );
              window.closePaymentModal?.();
            } catch (paymentError) {
              setError(paymentError.message);
            } finally {
              setIsLoading(false);
            }
          },
          onclose: () => {
            setNotice('Payment window was closed.');
            setIsLoading(false);
          },
        });
        return;
      }

      await runHostedOrMobileGateway(reference);
    } catch (paymentError) {
      console.error('Payment error:', paymentError);
      setError(
        paymentError?.response?.data?.message ||
          paymentError?.message ||
          'Unable to complete payment.'
      );
      setIsLoading(false);
    }
  };

  const unavailable = configLoading || !paymentConfig || !gatewayDetails;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg md:p-8">
      <h2 className="mb-6 text-2xl font-semibold text-primary">
        Complete Your Payment
      </h2>

      {gatewayDetails && (
        <div className="mb-6 flex items-center gap-4 rounded-2xl bg-gray-50 p-4">
          <img
            src={gatewayDetails.icon}
            alt={gatewayDetails.name}
            className="h-11 w-11 object-contain"
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
          />
          <div>
            <p className="text-lg font-semibold text-primary">
              Pay securely with {gatewayDetails.name}
            </p>
            <p className="text-sm text-gray-600">
              {gatewayDetails.description}
            </p>
            <p className="mt-1 text-xs font-bold text-gray-500">
              Currency: {currency}
            </p>
          </div>
        </div>
      )}

      <div className="mb-8 space-y-3 text-primary">
        <div className="flex justify-between text-sm">
          <span>Subtotal</span>
          <span>{money(total, currency)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span>VAT ({Number(vat || 0)}%)</span>
          <span>{money(vatAmount, currency)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span>Delivery</span>
          <span>{money(shippingFee, currency)}</span>
        </div>
        <div className="mt-2 flex justify-between border-t pt-3 text-lg font-semibold">
          <span>Total</span>
          <span>{money(finalTotal, currency)}</span>
        </div>
      </div>

      {error && (
        <p className="mb-4 rounded-xl bg-red-50 p-3 text-center text-sm text-red-700">
          {error}
        </p>
      )}
      {notice && !error && (
        <p className="mb-4 rounded-xl bg-blue-50 p-3 text-center text-sm text-blue-700">
          {notice}
        </p>
      )}

      <Button
        onClick={handlePayment}
        disabled={isLoading || disabled || unavailable}
        className="w-full rounded-full bg-primary py-4 text-lg text-cream disabled:cursor-not-allowed disabled:bg-gray-400"
      >
        {configLoading
          ? 'Loading payment method…'
          : isLoading
            ? 'Processing payment…'
            : disabled
              ? 'Complete Delivery Details First'
              : gatewayDetails
                ? `Pay ${money(finalTotal, currency)} with ${gatewayDetails.name}`
                : 'Payment unavailable'}
      </Button>

      <p className="mt-4 text-center text-xs text-gray-500">
        Payment is processed directly by the restaurant’s configured provider.
      </p>
    </div>
  );
}

export default PaymentForm;